/**
 * IL CUSTODE DELLA SESSIONE del sito clienti.
 *
 * Il server decide quanto dura un accesso (la regola di sessione della skin,
 * impostata dal backoffice) e lo dice con l'accesso e con ogni rinnovo:
 * quando scade il gettone, quando scade la sessione se non si fa niente,
 * quanti secondi prima avvisare, fino a quando si puo' rinnovare. Qui non si
 * inventa nessun tempo: si leggono quelli e si fa quello che dicono.
 *
 * COSA FA
 *   - tiene i tempi che il server manda (localStorage, stessi nomi del
 *     backoffice: sessione.scade_il, sessione.sessione_scade_il,
 *     sessione.durata_massima_fino_a, sessione.avviso_secondi,
 *     sessione.inattivita_secondi);
 *   - segna l'attivita' della persona (tocco, tasto, scorrimento);
 *   - rinnova il gettone prima che scada, ma SOLO se c'e' stata attivita':
 *     chi non fa niente non viene tenuto dentro per sbaglio;
 *   - avvisa «la sessione sta per scadere» quanti secondi prima dice il
 *     server, con «Resto collegato» che rinnova;
 *   - mette su axios le intestazioni dell'accesso (gettone, TENANT_ID,
 *     X-Client-Surface) per tutte le chiamate al gateway, e quando il
 *     gateway risponde 401 con un codice fa la cosa giusta: GETTONE_SCADUTO
 *     prova UN rinnovo e ripete la chiamata; SESSIONE_REVOCATA e gli altri
 *     chiudono, scrivendo il motivo;
 *   - quando la sessione finisce (per tempo, per revoca, per durata) lo dice
 *     con un motivo scritto, che la finestra di accesso mostra.
 *
 * COSA NON FA
 *   - non decide chi puo' entrare ne' da dove: lo dice il server;
 *   - non tocca la schedina: resta in sessionStorage e si ritrova rientrando;
 *   - non svuota da sola il profilo in localStorage: lo fa UserService, che
 *     sa cosa ha scritto, quando questo custode gli dice che e' finita.
 *
 * Il codice dell'errore arriva dal gateway o da user-management; il testo
 * per la persona sta qui sotto, in TESTI, un posto solo.
 */
import axios from 'axios';
import { BehaviorSubject } from 'rxjs';
import { frase } from '../frase';

const CHIAVI = {
  scadeIl: 'sessione.scade_il',
  sessioneScadeIl: 'sessione.sessione_scade_il',
  durataFinoA: 'sessione.durata_massima_fino_a',
  avvisoSecondi: 'sessione.avviso_secondi',
  inattivitaSecondi: 'sessione.inattivita_secondi',
};

/** Quanto prima della scadenza del gettone si rinnova, se c'e' stata attivita'. */
const ANTICIPO_RINNOVO_MS = 2 * 60 * 1000;
const BATTITO_MS = 5 * 1000;

/* «La sessione sta per scadere»: null, oppure { scadeIl, fermoDaMinuti }.
   La finestra in basso lo ascolta. */
const avviso$ = new BehaviorSubject(null);

/* «La sessione e' finita»: null, oppure { codice, titolo, testo }. Resta
   finche' qualcuno non lo consuma (la finestra di accesso, quando si apre):
   cosi' non si perde se succede prima che la barra sia montata. */
const fine$ = new BehaviorSubject(null);

let ultimaAttivita = Date.now();
let attivitaDalRinnovo = true;
let battito = null;
let rinnovoInCorso = null;
let avviato = false;
let intercettoriMontati = false;
let allaFine = null;

// ------------------------------------------------------------------ i testi

/**
 * Il testo per la persona, dal codice. Il motivo del server, se c'e', entra
 * nel testo. Le frasi sono segnate con frase() (src/frase.js) e si traducono
 * dove si scrivono (FinestraAccesso: t(titolo), t(testo, valori)), nella
 * lingua di chi guarda in quel momento: qui non c'e' ancora uno schermo.
 * `valori` porta i numeri che entrano nella frase (i minuti).
 */
export const TESTI = {
  SESSIONE_SCADUTA: () => {
    const minuti = Math.round(numero(CHIAVI.inattivitaSecondi) / 60);
    return {
      titolo: frase('Sessione scaduta per inattività'),
      testo: minuti > 0
        ? frase('Sei stato scollegato dopo {{count}} minuti senza attività. La schedina è conservata: accedi di nuovo per giocarla.')
        : frase('Sei stato scollegato per inattività. La schedina è conservata: accedi di nuovo per giocarla.'),
      valori: { count: minuti },
    };
  },
  SESSIONE_OPERATORE_FINITA: () => ({
    titolo: frase('La sessione da operatore è finita'),
    testo: frase('I 30 minuti sul conto del giocatore sono passati e la sessione si è chiusa da sola. Chiudi questa scheda e torna al backoffice: se serve ancora, premi di nuovo «Accesso al sito».'),
  }),
  DURATA_MASSIMA: () => ({
    titolo: frase('Durata massima raggiunta'),
    testo: frase('L’accesso ha una durata massima, decisa dalla skin: dopo quella si rientra comunque, anche se si sta giocando. La schedina è conservata.'),
  }),
  SESSIONE_REVOCATA: (messaggio) => {
    const m = (messaggio || '').toLowerCase();
    if (m.includes('nuovo accesso')) {
      return { titolo: frase('Sessione chiusa da un nuovo accesso'), testo: frase('Sei entrato da un altro dispositivo o da un’altra finestra, e questa skin tiene una sola sessione per utente. Puoi rientrare da qui: sarà l’altra a chiudersi.') };
    }
    if (m.includes('operatore')) {
      return { titolo: frase('Sessione chiusa da un operatore'), testo: frase('Un operatore ha chiuso la tua sessione. Puoi rientrare; se non riesci, il conto potrebbe essere sospeso.') };
    }
    if (m.includes('cambio password')) {
      return { titolo: frase('Password cambiata'), testo: frase('La password è stata cambiata e tutte le sessioni sono state chiuse. Rientra con quella nuova.') };
    }
    if (m.includes('uscita')) {
      return { titolo: frase('Sei uscito'), testo: frase('La sessione è stata chiusa con l’uscita. Rientra quando vuoi.') };
    }
    return { titolo: frase('Sessione chiusa'), testo: messaggio || frase('La sessione non è più valida. Rientra per continuare.') };
  },
  GETTONE_NON_VALIDO: () => ({ titolo: frase('Accesso non più valido'), testo: frase('Rientra per continuare. La schedina è conservata.') }),
  GETTONE_NON_DI_ACCESSO: () => ({ titolo: frase('Accesso non più valido'), testo: frase('Rientra per continuare. La schedina è conservata.') }),
  GETTONE_SCADUTO: () => ({ titolo: frase('Accesso scaduto'), testo: frase('Rientra per continuare. La schedina è conservata.') }),
  INTERFACCIA_NON_CONSENTITA: (messaggio) => ({ titolo: frase('Da qui non si entra'), testo: messaggio || frase('Questo conto non può usare il sito clienti.') }),
  CONTO_SOSPESO: (messaggio) => ({ titolo: frase('Conto sospeso'), testo: messaggio || frase('Il conto è sospeso. Rivolgiti a chi lo gestisce.') }),
  CONTO_NON_ATTIVO: (messaggio) => ({ titolo: frase('Conto non attivo'), testo: messaggio || frase('Il conto non è attivo.') }),
};

export function motivoDa(codice, messaggio) {
  const fa = TESTI[codice] || (() => ({ titolo: frase('Sessione finita'), testo: messaggio || frase('Rientra per continuare.') }));
  return { codice: codice || 'SCONOSCIUTO', ...fa(messaggio) };
}

// ------------------------------------------------------------------ gli attrezzi

function env() {
  return (typeof window !== 'undefined' && window.env) || {};
}

function gateway() {
  return env().REACT_APP_API_URL || '';
}

function tenant() {
  return env().REACT_APP_API_TENANT || 1;
}

/** L'indirizzo dell'accesso e del rinnovo: lo stesso di doLogin. */
export function urlAccesso() {
  return `${gateway()}/${env().REACT_APP_API_VERSION || 'v1'}/users-management-service/api/login/oauth`;
}

export function urlUscita() {
  return `${gateway()}/${env().REACT_APP_API_VERSION || 'v1'}/users-management-service/api/login/logout`;
}

function numero(chiave) {
  const v = window.localStorage.getItem(chiave);
  const n = v == null ? NaN : Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function gettone() {
  const t = window.localStorage.getItem('access_token');
  return t && t !== 'null' && t !== 'undefined' ? t : null;
}

function rinnovoSalvato() {
  const t = window.localStorage.getItem('refresh_token');
  return t && t !== 'null' && t !== 'undefined' ? t : null;
}

/** Vero se il gettone porta un operatore (claim «operatore»: un operatore del backoffice sul sito con il conto di un altro). */
export function operatoreDal(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !!payload.operatore;
  } catch (e) {
    return false;
  }
}

function scadenzaDal(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
}

/** Il gettone d'accesso e' scaduto (o sta per scadere entro 5 secondi)? */
export function gettoneScaduto() {
  const t = gettone();
  if (!t) return true;
  const exp = scadenzaDal(t);
  return exp > 0 && exp - 5000 < Date.now();
}

/** C'e' una sessione che si puo' riprendere? Gettone di rinnovo e sessione non ancora scaduta. */
export function sessioneRiprendibile() {
  const scade = numero(CHIAVI.sessioneScadeIl);
  return !!rinnovoSalvato() && (!scade || scade > Date.now());
}

// ------------------------------------------------------------------ i tempi

/** I tempi che il server ha mandato con l'accesso o col rinnovo. */
export function salvaTempi(r) {
  if (!r) return;
  if (r.scade_il) window.localStorage.setItem(CHIAVI.scadeIl, String(r.scade_il));
  if (r.sessione_scade_il) window.localStorage.setItem(CHIAVI.sessioneScadeIl, String(r.sessione_scade_il));
  if (r.durata_massima_fino_a) window.localStorage.setItem(CHIAVI.durataFinoA, String(r.durata_massima_fino_a));
  if (r.avviso_secondi !== undefined && r.avviso_secondi !== null) window.localStorage.setItem(CHIAVI.avvisoSecondi, String(r.avviso_secondi));
  if (r.inattivita_secondi !== undefined && r.inattivita_secondi !== null) window.localStorage.setItem(CHIAVI.inattivitaSecondi, String(r.inattivita_secondi));
  attivitaDalRinnovo = false;
  if (avviso$.getValue()) avviso$.next(null);
}

/** I gettoni e i tempi insieme, come arrivano dalla risposta dell'accesso o del rinnovo. */
export function salvaGettoni(r) {
  if (!r || !r.access_token) return;
  window.localStorage.setItem('access_token', r.access_token);
  if (r.refresh_token) window.localStorage.setItem('refresh_token', r.refresh_token);
  // CLIENT_SESSION e' il nome che il sito usa da sempre per l'identificativo
  // della sessione; il server oggi lo chiama `sid`
  if (r.sid) window.localStorage.setItem('CLIENT_SESSION', JSON.stringify(r.sid));
  // «fino a quando si puo' rinnovare»: la vecchia chiave, con il valore giusto
  if (r.sessione_scade_il) window.localStorage.setItem('refresh_token_expiration', String(r.sessione_scade_il));
  salvaTempi(r);
}

export function dimenticaTempi() {
  Object.values(CHIAVI).forEach((k) => window.localStorage.removeItem(k));
  if (avviso$.getValue()) avviso$.next(null);
}

// ------------------------------------------------------------------ l'attivita'

export function segnaAttivita() {
  ultimaAttivita = Date.now();
  attivitaDalRinnovo = true;
}

const EVENTI_ATTIVITA = ['pointerdown', 'keydown', 'touchstart', 'wheel'];

function ascoltaAttivita() {
  EVENTI_ATTIVITA.forEach((e) => window.addEventListener(e, segnaAttivita, { passive: true }));
}

function smettiDiAscoltare() {
  EVENTI_ATTIVITA.forEach((e) => window.removeEventListener(e, segnaAttivita));
}

// ------------------------------------------------------------------ il rinnovo

/**
 * Un rinnovo alla volta: dieci chiamate che trovano il gettone scaduto nello
 * stesso istante aspettano tutte lo stesso rinnovo. Risponde true se adesso
 * c'e' un gettone buono.
 */
export function rinnova() {
  if (rinnovoInCorso) return rinnovoInCorso;
  const refresh = rinnovoSalvato();
  if (!refresh) {
    fine('SESSIONE_SCADUTA');
    return Promise.resolve(false);
  }
  rinnovoInCorso = axios.post(urlAccesso(),
      { grant_type: 'REFRESH_TOKEN', refresh_token: refresh },
      { headers: { TENANT_ID: tenant(), 'Content-Type': 'application/json', 'X-Client-Surface': 'FRONTEND' } })
    .then((res) => {
      if (!res.data || !res.data.access_token) {
        fine((res.data && res.data.code) || 'GETTONE_NON_VALIDO', res.data && res.data.message);
        return false;
      }
      salvaGettoni(res.data);
      return true;
    })
    .catch((e) => {
      if (e && e.response) {
        // il server ha detto di no, e ha detto perche' (SESSIONE_SCADUTA,
        // SESSIONE_REVOCATA, DURATA_MASSIMA, CONTO_SOSPESO...)
        const d = e.response.data || {};
        fine(d.code || 'SESSIONE_SCADUTA', d.message);
      }
      // senza risposta (rete) non si butta fuori nessuno: si riprova al
      // prossimo battito, e se la sessione scade davvero lo dice l'orologio
      return false;
    })
    .finally(() => { rinnovoInCorso = null; });
  return rinnovoInCorso;
}

/** «Resto collegato»: conta come attivita' e rinnova subito. */
export function restoCollegato() {
  segnaAttivita();
  avviso$.next(null);
  return rinnova();
}

// ------------------------------------------------------------------ la fine

/**
 * La sessione e' finita, per un motivo. Ferma il custode, dimentica i
 * tempi, dice a UserService di svuotare il profilo e lascia il motivo
 * scritto per la finestra di accesso.
 */
export function fine(codice, messaggio) {
  const motivo = motivoDa(codice, messaggio);
  fermaCustode();
  dimenticaTempi();
  if (typeof allaFine === 'function') {
    try { allaFine(motivo); } catch (e) { /* niente: la fine non deve fallire */ }
  }
  fine$.next(motivo);
}

/** UserService dice cosa fare quando finisce (svuotare il profilo, spegnere loggedIn). */
export function collega({ allaFine: cb }) {
  allaFine = cb;
}

export function fineSessione() {
  return fine$.asObservable();
}

/** La finestra di accesso ha mostrato il motivo: non serve piu'. */
export function consumaFine() {
  if (fine$.getValue()) fine$.next(null);
}

export function avvisoSessione() {
  return avviso$.asObservable();
}

// ------------------------------------------------------------------ il battito

function controlla() {
  const token = gettone();
  if (!token) { fermaCustode(); return; }
  const adesso = Date.now();

  const durata = numero(CHIAVI.durataFinoA);
  // per una sessione da operatore (Accesso al sito) il termine e' il suo, e lo si dice con le sue parole
  if (durata && adesso >= durata) { fine(operatoreDal(token) ? 'SESSIONE_OPERATORE_FINITA' : 'DURATA_MASSIMA'); return; }

  const sessioneScade = numero(CHIAVI.sessioneScadeIl);
  if (sessioneScade && adesso >= sessioneScade) { fine('SESSIONE_SCADUTA'); return; }

  const avvisoSecondi = numero(CHIAVI.avvisoSecondi);
  if (sessioneScade && avvisoSecondi > 0 && !attivitaDalRinnovo
      && sessioneScade - adesso <= avvisoSecondi * 1000) {
    if (!avviso$.getValue()) {
      avviso$.next({ scadeIl: sessioneScade, fermoDaMinuti: Math.max(1, Math.round((adesso - ultimaAttivita) / 60000)) });
    }
  } else if (avviso$.getValue()) {
    avviso$.next(null);
  }

  // il rinnovo in anticipo, solo se la persona ha fatto qualcosa
  const scade = numero(CHIAVI.scadeIl) || scadenzaDal(token);
  if (scade && attivitaDalRinnovo && scade - adesso <= ANTICIPO_RINNOVO_MS) {
    rinnova();
  }
}

export function avviaCustode() {
  montaIntercettori();
  if (avviato) return;
  avviato = true;
  attivitaDalRinnovo = false;
  ultimaAttivita = Date.now();
  ascoltaAttivita();
  battito = window.setInterval(controlla, BATTITO_MS);
  controlla();
}

export function fermaCustode() {
  if (!avviato) return;
  avviato = false;
  smettiDiAscoltare();
  if (battito) { window.clearInterval(battito); battito = null; }
  if (avviso$.getValue()) avviso$.next(null);
}

// ------------------------------------------------------------------ axios

function nostra(url) {
  const base = gateway();
  return !!base && typeof url === 'string' && url.startsWith(base);
}

function eAccesso(url) {
  return typeof url === 'string' && url.includes('/api/login/');
}

/**
 * Le intestazioni dell'accesso su OGNI chiamata al gateway, anche quelle
 * che non le mettono da sole (ventidue file usano axios cosi' com'e'). Chi
 * le mette gia' non viene toccato. Il gettone scaduto si rinnova PRIMA di
 * chiamare, cosi' la chiamata parte buona invece di tornare indietro.
 */
export function montaIntercettori() {
  if (intercettoriMontati) return;
  intercettoriMontati = true;

  axios.interceptors.request.use(async (config) => {
    if (!nostra(config.url) || eAccesso(config.url)) return config;
    config.headers = config.headers || {};
    const h = config.headers;
    if (!h.TENANT_ID && !h.tenant_id) h.TENANT_ID = tenant();
    if (!h['X-Client-Surface']) h['X-Client-Surface'] = 'FRONTEND';

    let token = gettone();
    if (token && gettoneScaduto() && rinnovoSalvato()) {
      await rinnova();
      token = gettone();
    }
    const vecchio = h.Authorization;
    if (token && (!vecchio || /^Bearer /.test(vecchio))) {
      // anche se c'era gia': potrebbe essere il gettone di prima del rinnovo
      if (!vecchio || vecchio !== 'Bearer public') h.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use((r) => r, async (error) => {
    const r = error && error.response;
    const cfg = (error && error.config) || {};
    if (!r || !nostra(cfg.url) || eAccesso(cfg.url)) return Promise.reject(error);
    const code = r.data && r.data.code;

    if (r.status === 401) {
      // il gettone e' scaduto fra un controllo e l'altro: UN rinnovo e si ripete
      if ((code === 'GETTONE_SCADUTO' || (!code && gettoneScaduto())) && !cfg.__ritentata && rinnovoSalvato()) {
        cfg.__ritentata = true;
        const ok = await rinnova();
        if (ok) {
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${gettone()}`;
          return axios(cfg);
        }
        return Promise.reject(error);
      }
      if (code === 'SESSIONE_REVOCATA' || code === 'GETTONE_NON_VALIDO'
          || code === 'GETTONE_NON_DI_ACCESSO' || code === 'GETTONE_SCADUTO') {
        fine(code, r.data && r.data.message);
      } else if (code === 'GETTONE_MANCANTE' && gettone()) {
        fine('GETTONE_NON_VALIDO');
      }
      // un 401 senza codice e' di un servizio dietro al gateway, non del
      // gateway: non e' detto che la sessione sia finita, e non si butta
      // fuori nessuno per una chiamata andata male
      return Promise.reject(error);
    }

    if (r.status === 403 && code === 'INTERFACCIA_NON_CONSENTITA') {
      fine(code, r.data && r.data.message);
    }
    return Promise.reject(error);
  });
}

const Sessione = {
  CHIAVI,
  TESTI,
  motivoDa,
  urlAccesso,
  urlUscita,
  gettoneScaduto,
  sessioneRiprendibile,
  salvaTempi,
  salvaGettoni,
  dimenticaTempi,
  segnaAttivita,
  rinnova,
  restoCollegato,
  fine,
  collega,
  fineSessione,
  consumaFine,
  avvisoSessione,
  avviaCustode,
  fermaCustode,
  montaIntercettori,
};

export default Sessione;
